import { useState, type ReactNode } from 'react';

import { Modal } from '@/components/Modal';
import { EmptyCard, LoadingBlock, PageHeader } from '@/components/PageHeader';
import { SiteSearchSelect } from '@/components/SiteSearchSelect';
import { errorMessage, inputStyle } from '@/lib/format';
import { useSites } from '@/lib/queries';

import {
  labelOf,
  PART_LABELS,
  SOURCE_LABELS,
  summaryOf,
  useLegacyLink,
  useLegacyRecord,
  useLegacyRecords,
  useLegacySites,
  useLegacySources,
  type LegacyDetail,
  type LegacyScope,
} from './legacyApi';

/**
 * 옛 기록 보관함(V592 · DOC-136 · 사용자 결정 2026-10-28) — 지운 옛 안전보건관리시스템의 기록을 읽기만 한다.
 *
 * <p>전체 화면(/legacy · 안전 권한)은 왼쪽에 옛 현장 목록, 현장 화면(/sites/:id/legacy)은 그 현장에 붙은 기록만.
 * 우리 현장과 이름이 같은 옛 현장만 현장 화면에 붙는다 — 나머지는 전체 화면에서 옛 이름으로 본다.
 */
export function LegacyArchivePage({ siteId }: { siteId?: string }) {
  const sites = useLegacySites(!siteId);
  const [site, setSite] = useState<string | null>(null);
  const scope: LegacyScope = siteId ? { siteId } : { site };
  const sources = useLegacySources(scope);
  const [source, setSource] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const records = useLegacyRecords(scope, source, from, to, page);
  const [open, setOpen] = useState<{ source: string; code: string } | null>(null);
  const ourSites = useSites(!siteId);
  const link = useLegacyLink();
  const picked = (sites.data ?? []).find((s) => s.siteName === site) ?? null;

  const pick = (next: () => void) => {
    next();
    setPage(1);
  };
  const data = records.data;
  const pages = data ? Math.max(1, Math.ceil(data.total / data.size)) : 1;

  return (
    <div className="space-y-5">
      <style>{STYLES}</style>
      <PageHeader title="기존 데이터" />
      <div className={siteId ? '' : 'lg-wrap'}>
        {!siteId && (
          <aside className="card lg-sites">
            {sites.isPending ? (
              <LoadingBlock />
            ) : (
              <>
                <button type="button" className={`lg-site ${site === null ? 'on' : ''}`} onClick={() => pick(() => setSite(null))}>
                  <span>전체</span>
                  <b>{(sites.data ?? []).reduce((n, s) => n + s.count, 0).toLocaleString()}</b>
                </button>
                {(sites.data ?? []).map((s) => (
                  <button
                    key={`${s.siteName}:${s.siteId ?? ''}`}
                    type="button"
                    className={`lg-site ${site === s.siteName ? 'on' : ''}`}
                    onClick={() => pick(() => setSite(s.siteName))}
                    title={`${s.firstDate ?? ''} ~ ${s.lastDate ?? ''}`}
                  >
                    <span>{s.siteName || '(현장 없음)'}</span>
                    <b>{s.count.toLocaleString()}</b>
                  </button>
                ))}
              </>
            )}
          </aside>
        )}

        <section className="space-y-3" style={{ minWidth: 0 }}>
          {/* 옛 현장 ↔ 우리 현장 짝짓기 — 붙이면 그 현장 화면(메뉴 목록 · 홈 · 분석)에 옛 기록이 섞인다(DOC-136 §7). */}
          {!siteId && picked && (
            <div className="card flex flex-wrap items-center gap-3 px-4 py-3 text-ui-sm">
              <b>{picked.siteName || '(현장 없음)'}</b>
              <span style={{ color: 'var(--text-secondary)' }}>→ 우리 현장</span>
              <div style={{ width: '320px' }}>
                <SiteSearchSelect
                  sites={ourSites.data ?? []}
                  value={picked.siteId ?? ''}
                  onChange={(value) => link.mutate({ siteName: picked.siteName, siteId: value || null })}
                  extras={[{ value: '', label: '연결 안 함' }]}
                  width="320px"
                  className="input"
                  buttonStyle={{ ...inputStyle, minHeight: '36px' }}
                />
              </div>
              {link.isError && <span style={{ color: 'var(--danger)' }}>{errorMessage(link.error)}</span>}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <div className="lg-chips">
              <button type="button" className={source === '' ? 'on' : ''} onClick={() => pick(() => setSource(''))}>
                전체
              </button>
              {(sources.data ?? []).map((s) => (
                <button key={s.source} type="button" className={source === s.source ? 'on' : ''} onClick={() => pick(() => setSource(s.source))}>
                  {SOURCE_LABELS[s.source] ?? s.source} <span className="lg-count">{s.count.toLocaleString()}</span>
                </button>
              ))}
            </div>
            <input type="date" className="input" style={{ ...inputStyle, width: '150px' }} value={from} onChange={(e) => pick(() => setFrom(e.target.value))} aria-label="시작일" />
            <span style={{ color: 'var(--text-muted)' }}>~</span>
            <input type="date" className="input" style={{ ...inputStyle, width: '150px' }} value={to} onChange={(e) => pick(() => setTo(e.target.value))} aria-label="종료일" />
          </div>

          {records.isPending ? (
            <LoadingBlock />
          ) : records.isError ? (
            <LoadingBlock />
          ) : !data || data.rows.length === 0 ? (
            <EmptyCard>기록이 없습니다.</EmptyCard>
          ) : (
            <div className="card p-0" style={{ overflowX: 'auto' }}>
              <table className="site-table lg-table">
                <thead>
                  <tr>
                    <th style={{ width: '104px' }}>일자</th>
                    <th style={{ width: '150px' }}>메뉴</th>
                    {!siteId && <th style={{ textAlign: 'left' }}>현장</th>}
                    <th>업체</th>
                    <th>공종</th>
                    <th style={{ textAlign: 'left' }}>내용</th>
                    <th>작성자</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row) => (
                    <tr key={`${row.source}:${row.code}`} onClick={() => setOpen({ source: row.source, code: row.code })}>
                      <td>{row.recordDate ?? '—'}</td>
                      <td>{SOURCE_LABELS[row.source] ?? row.source}</td>
                      {!siteId && <td style={{ textAlign: 'left' }}>{row.siteName ?? '—'}</td>}
                      <td>{text(row.listRow.cpname)}</td>
                      <td>{text(row.listRow.gmname ?? row.listRow.gname)}</td>
                      <td style={{ textAlign: 'left' }} className="lg-summary">
                        {summaryOf(row.listRow) || '—'}
                      </td>
                      <td>{text(row.listRow.sfname)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex items-center justify-end gap-2 px-4 py-3 text-ui-sm">
                <span style={{ color: 'var(--text-secondary)' }}>
                  {data.total.toLocaleString()}건 · {page}/{pages}
                </span>
                <button type="button" className="btn-secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  이전
                </button>
                <button type="button" className="btn-secondary" disabled={page >= pages} onClick={() => setPage(page + 1)}>
                  다음
                </button>
              </div>
            </div>
          )}
        </section>
      </div>

      {open && <LegacyRecordModal source={open.source} code={open.code} onClose={() => setOpen(null)} />}
    </div>
  );
}

function text(value: unknown): string {
  return typeof value === 'string' && value.trim() ? value : '—';
}

/** 기록 한 장 — 본문 칸(이름표가 있는 것만) · 사진 · 첨부 · 결재 · 그 밖의 줄 표. */
export function LegacyRecordModal({ source, code, onClose }: { source: string; code: string; onClose: () => void }) {
  const record = useLegacyRecord(source, code);
  const d = record.data;
  return (
    <Modal title={`${SOURCE_LABELS[source] ?? source} · ${d?.recordDate ?? ''}`} onClose={onClose} maxWidth="1100px">
      {record.isPending ? (
        <LoadingBlock />
      ) : !d ? (
        <LoadingBlock />
      ) : (
        <RecordBody record={d} />
      )}
    </Modal>
  );
}

function RecordBody({ record }: { record: LegacyDetail }) {
  const fields = Object.entries(record.listRow)
    .map(([k, v]) => [labelOf(record.source, k), v] as const)
    .filter(([label, v]) => label && typeof v === 'string' && v.trim()) as Array<[string, string]>;
  const parts = Object.entries(record.detail ?? {}).filter(([key]) => !/_Row$|_Sign_Check/.test(key));
  const photos = Object.values(record.files).filter((f) => /\.(jpe?g|png|gif|bmp|webp)$/i.test(f.name) || !/\.\w{2,4}$/.test(f.name));
  const attachments = Object.values(record.files).filter((f) => !photos.includes(f) && !/^sf_\d+\.png$/.test(f.name));
  return (
    <div className="space-y-5">
      <style>{STYLES}</style>
      <table className="lg-fields">
        <tbody>
          {!fields.some(([label]) => label.replace(/\s/g, '').includes('현장')) && (
            <tr>
              <th>현장</th>
              <td>{record.siteName ?? '—'}</td>
            </tr>
          )}
          {fields.map(([label, value], i) => (
            <tr key={`${label}:${i}`}>
              <th>{label}</th>
              <td style={{ whiteSpace: 'pre-wrap' }}>{value}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {photos.length > 0 && (
        <Part title={`사진 ${photos.length}장`}>
          <div className="lg-photos">
            {photos.map((f) =>
              f.url ? (
                <a key={f.name} href={f.url} target="_blank" rel="noreferrer">
                  <img src={f.url} alt={f.name} />
                </a>
              ) : (
                <div key={f.name} className="lg-nophoto" title={f.name}>
                  {f.missing ? '원본 없음' : '불러오는 중입니다'}
                </div>
              ),
            )}
          </div>
        </Part>
      )}

      {attachments.length > 0 && (
        <Part title="첨부">
          <ul className="lg-files">
            {attachments.map((f) => (
              <li key={f.name}>
                {f.url ? (
                  <a href={f.url} target="_blank" rel="noreferrer">
                    {f.name}
                  </a>
                ) : (
                  <span style={{ color: 'var(--text-muted)' }}>{f.name}</span>
                )}
                {f.bytes ? <span style={{ color: 'var(--text-muted)' }}> · {(f.bytes / 1048576).toFixed(1)}MB</span> : null}
              </li>
            ))}
          </ul>
        </Part>
      )}

      {parts
        .filter(([key]) => !/MainImg_List|MainAttack_List/.test(key))
        .map(([key, value]) => {
          const rows = (Array.isArray(value) ? value : [value]) as Array<Record<string, unknown>>;
          const title = PART_LABELS.find(([re]) => re.test(key))?.[1] ?? key;
          const sign = /SignMain/.test(key);
          const cols = [...new Set(rows.flatMap((r) => Object.keys(r ?? {})))].filter((k) => labelOf(record.source, k));
          if (rows.length === 0 || (cols.length === 0 && !sign)) return null;
          return (
            <Part key={key} title={title}>
              <table className="site-table">
                <thead>
                  <tr>
                    {cols.map((c) => (
                      <th key={c}>{labelOf(record.source, c)}</th>
                    ))}
                    {sign && <th>서명</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i}>
                      {cols.map((c) => (
                        <td key={c} style={{ whiteSpace: 'pre-wrap', textAlign: 'left' }}>
                          {text(r?.[c])}
                        </td>
                      ))}
                      {sign && (
                        <td>
                          {typeof r?.signimg === 'string' && record.files[r.signimg]?.url ? (
                            <img src={record.files[r.signimg]!.url!} alt="서명" style={{ height: '32px', margin: '0 auto' }} />
                          ) : (
                            '—'
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Part>
          );
        })}
    </div>
  );
}

function Part({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <div style={{ fontSize: '14px', fontWeight: 700, margin: '0 0 8px' }}>{title}</div>
      {children}
    </section>
  );
}

const STYLES = `
.lg-wrap { display: grid; grid-template-columns: 280px minmax(0, 1fr); gap: 16px; align-items: start; }
.lg-sites { padding: 8px; max-height: calc(100vh - 160px); overflow-y: auto; }
.lg-site { display: flex; justify-content: space-between; gap: 8px; width: 100%; padding: 8px 10px; border: none; background: none; border-radius: 8px; font-size: 13px; text-align: left; cursor: pointer; }
.lg-site:hover { background: var(--bg-hover, #f1f5f9); }
.lg-site.on { background: var(--accent-bg); color: var(--accent); font-weight: 600; }
.lg-site b { font-weight: 500; color: var(--text-muted); }
.lg-chips { display: inline-flex; flex-wrap: wrap; gap: 4px; padding: 4px; border-radius: 10px; background: var(--bg-hover, #f1f5f9); }
.lg-chips button { padding: 6px 10px; border: none; border-radius: 8px; background: transparent; font-size: 13px; cursor: pointer; }
.lg-chips button.on { background: var(--bg-card, #fff); font-weight: 700; box-shadow: 0 1px 2px rgba(0,0,0,.08); }
.lg-count { color: var(--text-muted); }
.lg-table tbody tr { cursor: pointer; }
.lg-table tbody tr:hover { background: var(--bg-hover, #f8fafc); }
.lg-summary { max-width: 520px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.lg-fields { width: 100%; border-collapse: collapse; font-size: 13px; }
.lg-fields th { width: 180px; text-align: left; vertical-align: top; padding: 7px 10px; background: #f8fafc; border: 1px solid var(--border); font-weight: 600; color: var(--text-secondary); }
.lg-fields td { padding: 7px 10px; border: 1px solid var(--border); }
.lg-photos { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 8px; }
.lg-photos img { width: 100%; height: 150px; object-fit: cover; border-radius: 8px; border: 1px solid var(--border); }
.lg-nophoto { height: 150px; display: flex; align-items: center; justify-content: center; border: 1px dashed var(--border); border-radius: 8px; font-size: 12px; color: var(--text-muted); }
.lg-files { list-style: none; padding: 0; margin: 0; font-size: 13px; }
.lg-files li { padding: 4px 0; }
@media (max-width: 900px) { .lg-wrap { grid-template-columns: minmax(0, 1fr); } }
`;
